(function () {
  'use strict';

  // Column searched against. Must match the Google Sheet header exactly.
  var SEARCH_KEY = 'Batch Code';

  // Shown first, with no heading.
  // 'Product image' is a VLOOKUP column added to the main sheet (product name ->
  // Shopify image URL). The https-only guard hides it when the value is blank or
  // an error like "#N/A" (no match), so batches without a packshot just render fewer rows.
  var IDENTIFIER_FIELDS = [
    { key: ['Product Image', 'Product image'], label: 'Product image', type: 'image' },
    { key: SEARCH_KEY, label: 'Batch Code', type: 'text' },
    // Sheet header currently includes the format hint; keep the old plain header
    // as a fallback in case it is renamed back. Stored UK DD/MM/YYYY; displayed in
    // the format set by the section's "Expiration date display format" setting.
    { key: ['Expiration date (DD/MM/YY)', 'Expiration date'], label: 'Expiration date', type: 'date' },
    // Display the mapped "clean" title (sheet VLOOKUP from the raw name); fall back
    // to the raw "Product name" if a clean mapping is missing.
    { key: ['Clean Product Name', 'Product name'], label: 'Product name', type: 'text' }
  ];

  // Test/certification groups, in display order.
  // A field `key` may be a string OR an array of candidate keys (first non-blank
  // wins) — used for the Sulfite transitional fallback. `cert` fields carry a
  // `logo` naming a data-logo-* source built in init().
  var GROUPS = [
    { heading: 'Heavy Metals', intro: 'heavy_metals', fields: [
      { key: 'Arsenic', label: 'Arsenic', type: 'result' },
      { key: 'Cadmium', label: 'Cadmium', type: 'result' },
      { key: 'Lead', label: 'Lead', type: 'result' },
      { key: 'Mercury', label: 'Mercury', type: 'result' }
    ] },
    { heading: 'Pathogens', intro: 'pathogens', fields: [
      { key: 'Cronobacter', label: 'Cronobacter', type: 'result' },
      { key: 'Salmonella', label: 'Salmonella', type: 'result' },
      { key: 'Listeria Monocytogenes', label: 'Listeria Monocytogenes', type: 'result' }
    ] },
    { heading: 'Toxin-Producing Spore-Forming Bacteria', intro: 'toxin', fields: [
      { key: 'Bacillus cereus', label: 'Bacillus cereus', type: 'result' },
      // Transitional fallback: prefer the corrected header, fall back to the old
      // misspelled "Selfite" header through the sheet rename + SheetDB cache window.
      { key: ['Sulfite-Reducing Clostridia (SRC)', 'Selfite-reducing clostridia (SRC)'],
        label: 'Sulfite-Reducing Clostridia (SRC)', type: 'result' }
    ] },
    { heading: 'Hygiene Indicators', intro: 'hygiene', fields: [
      { key: 'Enterobacteriaceae', label: 'Enterobacteriaceae', type: 'result' },
      { key: 'Aerobic Plate Count', label: 'Aerobic Plate Count', type: 'result' }
    ] },
    { heading: 'Pesticides & Glyphosate', intro: 'pesticides', fields: [
      { key: 'Glyphosate', label: 'Glyphosate', type: 'result' },
      { key: 'Pesticides', label: 'Pesticides', type: 'result' }
    ] },
    { heading: 'Plasticizers', intro: 'plasticizers', fields: [
      { key: 'BPA', label: 'BPA', type: 'result' },
      { key: 'BPS', label: 'BPS', type: 'result' },
      { key: 'Phthalates', label: 'Phthalates', type: 'result' }
    ] },
    { heading: 'Dairy', intro: 'dairy', fields: [
      // NOTE: trailing space in the sheet header is intentional and required.
      { key: 'Alkaline Phosphatase ', label: 'Alkaline Phosphatase', type: 'result' },
      { key: 'Antibiotic testing', label: 'Antibiotic testing', type: 'result' },
      { key: 'SRC', label: 'SRC', type: 'result' },
      { key: 'Bacillus cereus', label: 'Bacillus cereus', type: 'result' }
    ] },
    { heading: 'Certifications', collapsible: false, className: 'certifications-wrapper', fields: [
      { key: 'EU organic logo', label: 'EU Organic', type: 'cert', logo: 'eu_organic' },
      { key: 'USDA organic logo', label: 'USDA Organic', type: 'cert', logo: 'usda_organic' },
      { key: ['Clean Label Project Purity Award', 'Clean label purity project award'], label: 'Clean Label Purity Project', type: 'cert', logo: 'clean_label' },
      { key: ['Halal', 'Halal accredited'], label: 'Halal', type: 'cert', logo: 'halal' },
      { key: 'Kosher', label: 'Kosher', type: 'cert', logo: 'kosher' },
      { key: 'Clean Label Project Pesticide Free Award', label: 'Clean Label Pesticide Free', type: 'cert', logo: 'clean_label_pesticide' },
      { key: 'Vegetarian', label: 'Vegetarian', type: 'cert', logo: 'vegetarian' }
    ] }
  ];

  // Status icons (decorative; sized via CSS .lot-lookup__icon).
  var ICON_TICK = '<svg class="lot-lookup__icon lot-lookup__icon--tick" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><circle cx="20.2655" cy="20.2655" r="20.2655" fill="#15681A"/><path d="M8.26648 21.5269L15.9895 28.6916L32.7983 12.1593" stroke="white" stroke-width="3.04004" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_CROSS = '<svg class="lot-lookup__icon lot-lookup__icon--cross" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><circle cx="20.2655" cy="20.2655" r="20.2655" fill="#EA5858"/><path d="M11 29L30 11M28.5012 29L12.2502 11" stroke="white" stroke-width="3.04004" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function normalizeLotCode(raw) {
    return (raw === null || raw === undefined ? '' : String(raw)).trim();
  }

  function buildSearchUrl(endpoint, code) {
    var base = String(endpoint).replace(/\/+$/, '');
    return base + '/search?' +
      encodeURIComponent(SEARCH_KEY) + '=' + encodeURIComponent(code) +
      '&casesensitive=false';
  }

  function escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isBlank(value) {
    return value === null || value === undefined || String(value).trim() === '';
  }

  // Whether the value has the word "No" (case-insensitive, whole word). Used to hide
  // a test result — "No" means the test doesn't apply to this product.
  function hasNo(value) {
    return /\bno\b/i.test(String(value === null || value === undefined ? '' : value));
  }

  // Only https URLs may be used as an image src for untrusted sheet data.
  function isSafeUrl(value) {
    return /^https:\/\//i.test(String(value === null || value === undefined ? '' : value).trim());
  }

  function pickRow(data) {
    return (Array.isArray(data) && data.length > 0) ? data[0] : null;
  }

  // Resolve a field value. `key` may be a string or an array of candidate keys
  // (first non-blank wins). Returns '' when nothing matches.
  function getValue(row, key) {
    if (Array.isArray(key)) {
      for (var i = 0; i < key.length; i++) {
        if (!isBlank(row[key[i]])) return row[key[i]];
      }
      return '';
    }
    var v = row[key];
    return (v === null || v === undefined) ? '' : v;
  }

  // Region (US/UK/CA/EU) -> the sheet column holding that region's packshot.
  // Defaults to US (this theme's region) when unset.
  function imageColumnForRegion(region) {
    return 'Product Image ' + (region || 'US');
  }

  // Stored dates are UK DD/MM/YYYY. When format is 'us', display as MM/DD/YYYY.
  // Anything that is not a recognisable D/M/Y triple is returned unchanged.
  function formatDate(value, format) {
    var s = String(value === null || value === undefined ? '' : value).trim();
    if (format !== 'us') return s;
    var m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (!m) return s;
    return m[2] + '/' + m[1] + '/' + m[3];
  }

  function renderField(row, field, logos, dateFormat, fallbackImage) {
    logos = logos || {};
    var value = getValue(row, field.key);

    if (field.type === 'image') {
      // Use the real (https) image; else the theme's fallback image; else hide.
      var imgUrl = (!isBlank(value) && isSafeUrl(value)) ? value
        : (!isBlank(fallbackImage) ? fallbackImage : '');
      if (!imgUrl) return '';
      // No visible label — the image speaks for itself (label kept as alt text).
      return '<div class="lot-lookup__row lot-lookup__row--image">' +
        '<span class="lot-lookup__value lot-lookup__image">' +
        '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(field.label) + '" loading="lazy"></span>' +
        '</div>';
    }

    if (isBlank(value)) return '';

    // A test/cert whose result has the word "No" is hidden (logo + text). A group
    // whose fields are all hidden drops out entirely (renderGroups skips empties).
    if ((field.type === 'result' || field.type === 'cert') && hasNo(value)) return '';

    if (field.type === 'cert') {
      var logoUrl = logos[field.logo];
      // Logo on top (decorative — the visible name label below names it), then the
      // cert name, then "Result: <value>" + tick. Value gates visibility (above).
      var logoHtml = isBlank(logoUrl) ? '' :
        '<span class="lot-lookup__cert-logo">' +
        '<img src="' + escapeHtml(logoUrl) + '" alt="" loading="lazy"></span>';
      return '<div class="lot-lookup__row lot-lookup__row--cert">' +
        logoHtml +
        '<span class="lot-lookup__label">' + escapeHtml(field.label) + '</span>' +
        '<span class="lot-lookup__result">' +
          '<span class="lot-lookup__result-label">Result:</span>' +
          '<span class="lot-lookup__result-value">' + escapeHtml(value) + ICON_TICK + '</span>' +
        '</span>' +
      '</div>';
    }

    if (field.type === 'result') {
      // "Result:" label, then the value + tick on the next line.
      return '<div class="lot-lookup__row lot-lookup__row--result">' +
        '<span class="lot-lookup__label">' + escapeHtml(field.label) + '</span>' +
        '<span class="lot-lookup__value lot-lookup__result">' +
          '<span class="lot-lookup__result-label">Result:</span>' +
          '<span class="lot-lookup__result-value">' + escapeHtml(value) + ICON_TICK + '</span>' +
        '</span>' +
      '</div>';
    }

    if (field.type === 'date') {
      value = formatDate(value, dateFormat);
    }

    return '<div class="lot-lookup__row">' +
      '<span class="lot-lookup__label">' + escapeHtml(field.label) + ':</span>' +
      '<span class="lot-lookup__value">' + escapeHtml(value) + '</span>' +
      '</div>';
  }

  function groupFieldsHtml(row, group, logos, dateFormat, fallbackImage) {
    return group.fields.map(function (field) {
      return renderField(row, field, logos, dateFormat, fallbackImage);
    }).join('');
  }

  // Collapsible accordion group: a toggle button + an animatable panel.
  // group.introHtml (trusted richtext from theme settings) renders inside the panel,
  // above the test rows.
  function accordionGroup(group, fieldsHtml, open, index) {
    var pid = 'lot-lookup-panel-' + index;
    var intro = isBlank(group.introHtml) ? '' :
      '<div class="lot-lookup__group-intro">' + group.introHtml + '</div>';
    return '<div class="lot-lookup__group lot-lookup__group--accordion' + (open ? ' is-open' : '') + '">' +
      '<button type="button" class="lot-lookup__group-toggle" aria-expanded="' + (open ? 'true' : 'false') +
        '" aria-controls="' + pid + '">' +
        '<span class="lot-lookup__group-heading">' + escapeHtml(group.heading) + '</span>' +
        '<span class="lot-lookup__group-status">Passed' + ICON_TICK + '</span>' +
        '<span class="lot-lookup__group-icon" aria-hidden="true"></span>' +
      '</button>' +
      '<div class="lot-lookup__group-panel" id="' + pid + '">' +
        '<div class="lot-lookup__group-panel-inner">' + intro + fieldsHtml + '</div>' +
      '</div>' +
    '</div>';
  }

  // Always-visible (non-collapsible) group, e.g. Certifications.
  function plainGroup(group, fieldsHtml) {
    var cls = 'lot-lookup__group' + (group.className ? ' ' + group.className : '');
    return '<div class="' + cls + '">' +
      '<span class="lot-lookup__group-heading">' + escapeHtml(group.heading) + '</span>' +
      '<div class="lot-lookup__cert-grid">' + fieldsHtml + '</div>' +
      '</div>';
  }

  function renderGroup(row, group, logos, dateFormat, fallbackImage) {
    var fields = groupFieldsHtml(row, group, logos, dateFormat, fallbackImage);
    if (!fields) return ''; // all fields blank -> skip the group entirely
    return group.collapsible === false
      ? plainGroup(group, fields)
      : accordionGroup(group, fields, false, 0);
  }

  function renderGroups(row, identifiers, groups, logos, dateFormat, fallbackImage, groupsHeading) {
    var head = identifiers.map(function (field) {
      return renderField(row, field, logos, dateFormat, fallbackImage);
    }).join('');
    // Identifiers (image, batch code, expiration date, product name) get their own
    // wrapper so they can be styled independently of the test groups.
    var headBlock = head ? '<div class="lot-lookup__identifiers">' + head + '</div>' : '';

    // Optional configurable heading (trusted richtext from theme settings, injected
    // raw), above the accordions and below the identifiers.
    var headingBlock = isBlank(groupsHeading) ? '' :
      '<div class="lot-lookup__groups-heading">' + groupsHeading + '</div>';

    var openedFirst = false;
    var groupsBody = ''; // all groups (accordions + Certifications), in order
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var fields = groupFieldsHtml(row, group, logos, dateFormat, fallbackImage);
      if (!fields) continue; // all blank -> skip
      if (group.collapsible === false) {
        groupsBody += plainGroup(group, fields);
      } else {
        // First non-empty collapsible group opens on load; the rest start closed.
        var open = !openedFirst;
        openedFirst = true;
        groupsBody += accordionGroup(group, fields, open, i);
      }
    }
    // Wrap all groups (accordions + Certifications) in one parent container.
    var body = groupsBody ? '<div class="lot-lookup__accordions">' + groupsBody + '</div>' : '';
    return '<div class="lot-lookup__card">' + headBlock + headingBlock + body + '</div>';
  }

  // Icon shown inside the input after a search: tick on match, cross on no-match,
  // nothing otherwise (e.g. network error).
  function statusIcon(outcome) {
    if (outcome === 'match') return ICON_TICK;
    if (outcome === 'no-match') return ICON_CROSS;
    return '';
  }

  function renderMessage(text, modifier) {
    var cls = 'lot-lookup__message' + (modifier ? ' lot-lookup__message--' + modifier : '');
    return '<div class="' + cls + '">' + escapeHtml(text) + '</div>';
  }

  // Exposed for the test harness.
  window.LotCodeLookup = {
    SEARCH_KEY: SEARCH_KEY,
    IDENTIFIER_FIELDS: IDENTIFIER_FIELDS,
    GROUPS: GROUPS,
    normalizeLotCode: normalizeLotCode,
    buildSearchUrl: buildSearchUrl,
    escapeHtml: escapeHtml,
    isBlank: isBlank,
    hasNo: hasNo,
    isSafeUrl: isSafeUrl,
    pickRow: pickRow,
    getValue: getValue,
    imageColumnForRegion: imageColumnForRegion,
    statusIcon: statusIcon,
    formatDate: formatDate,
    renderField: renderField,
    renderGroup: renderGroup,
    renderGroups: renderGroups,
    renderMessage: renderMessage
  };

  function init(root) {
    var form = root.querySelector('[data-lot-lookup-form]');
    var input = root.querySelector('[data-lot-lookup-input]');
    var result = root.querySelector('[data-lot-lookup-result]');
    var status = root.querySelector('[data-lot-lookup-status]');
    var endpoint = root.getAttribute('data-endpoint');
    var searchingMsg = root.getAttribute('data-searching') || 'Searching…';
    var notFoundMsg = root.getAttribute('data-not-found') ||
      'No match found. Please re-check the batch code and try again.';
    var errorMsg = root.getAttribute('data-error') ||
      'Something went wrong. Please try again.';

    if (!form || !input || !result || !endpoint) return;

    function setStatus(outcome) { if (status) status.innerHTML = statusIcon(outcome); }
    // Clear the input status icon as soon as the user edits the code.
    input.addEventListener('input', function () { setStatus(''); });

    // Accordion toggling — one delegated listener so it survives every re-render.
    // Independent: toggling one group does not affect the others.
    result.addEventListener('click', function (event) {
      var el = event.target;
      while (el && el !== result &&
        !(el.className && String(el.className).indexOf('lot-lookup__group-toggle') !== -1)) {
        el = el.parentNode;
      }
      if (!el || el === result) return;
      var group = el.parentNode;
      if (String(group.className).indexOf('is-open') !== -1) {
        group.className = group.className.replace(/\s*\bis-open\b/, '');
        el.setAttribute('aria-expanded', 'false');
      } else {
        group.className += ' is-open';
        el.setAttribute('aria-expanded', 'true');
      }
    });

    // 'us' -> reformat UK-stored dates to MM/DD/YYYY; otherwise display as entered.
    var dateFormat = root.getAttribute('data-date-format') || 'us';

    // Placeholder image shown when a batch has no product image.
    var fallbackImage = root.getAttribute('data-fallback-image') || '';

    // Region (US/UK/CA/EU) selects which sheet column holds this store's packshot.
    var region = root.getAttribute('data-region') || 'US';
    var imageColumn = imageColumnForRegion(region);
    var identifierFields = IDENTIFIER_FIELDS.map(function (field) {
      return field.type === 'image'
        ? { key: imageColumn, label: field.label, type: 'image' }
        : field;
    });

    // Logo URLs come from theme settings via data-logo-* attributes.
    var logos = {
      eu_organic: root.getAttribute('data-logo-eu-organic') || '',
      usda_organic: root.getAttribute('data-logo-usda-organic') || '',
      clean_label: root.getAttribute('data-logo-clean-label') || '',
      halal: root.getAttribute('data-logo-halal') || '',
      kosher: root.getAttribute('data-logo-kosher') || '',
      clean_label_pesticide: root.getAttribute('data-logo-clean-label-pesticide') || '',
      vegetarian: root.getAttribute('data-logo-vegetarian') || ''
    };

    // Configurable heading above the accordions + per-accordion intro richtext.
    var groupsHeading = root.getAttribute('data-groups-heading') || '';
    var intros = {
      heavy_metals: root.getAttribute('data-intro-heavy-metals') || '',
      pathogens: root.getAttribute('data-intro-pathogens') || '',
      toxin: root.getAttribute('data-intro-toxin') || '',
      hygiene: root.getAttribute('data-intro-hygiene') || '',
      pesticides: root.getAttribute('data-intro-pesticides') || '',
      plasticizers: root.getAttribute('data-intro-plasticizers') || '',
      dairy: root.getAttribute('data-intro-dairy') || ''
    };
    // Resolve each group's intro HTML onto a per-render copy so renderGroups stays lean.
    var groupsForRender = GROUPS.map(function (group) {
      if (!group.intro || isBlank(intros[group.intro])) return group;
      var copy = { heading: group.heading, fields: group.fields, introHtml: intros[group.intro] };
      if (group.collapsible === false) copy.collapsible = false;
      return copy;
    });

    var inFlight = null;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var code = normalizeLotCode(input.value);
      if (!code) { result.innerHTML = ''; setStatus(''); return; }

      if (inFlight) inFlight.abort();
      inFlight = (typeof AbortController !== 'undefined') ? new AbortController() : null;

      result.innerHTML = renderMessage(searchingMsg);
      setStatus('');

      fetch(buildSearchUrl(endpoint, code), {
        headers: { Accept: 'application/json' },
        signal: inFlight ? inFlight.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (data) {
          var row = pickRow(data);
          result.innerHTML = row
            ? renderGroups(row, identifierFields, groupsForRender, logos, dateFormat, fallbackImage, groupsHeading)
            : renderMessage(notFoundMsg, 'not-found');
          setStatus(row ? 'match' : 'no-match');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          result.innerHTML = renderMessage(errorMsg, 'error');
          setStatus('');
        });
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-lot-lookup]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
